import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { api } from '../config/api';

export interface AuthUser {
  uid: string;
  email: string;
  displayName: string | null;
  phoneNumber?: string | null;
  emailVerified: boolean;
  disabled: boolean;
  customClaims?: Record<string, any>;
  metadata?: {
    creationTime: string;
    lastSignInTime: string | null;
    lastRefreshTime?: string | null;
  };
  // For backward compatibility
  creationTime: string;
  lastSignInTime: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  authUsers: AuthUser[];
  userRole: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  createUser: (name: string, email: string, password: string) => Promise<User>;
  createAuthUser: (userData: {
    email: string;
    password?: string;
    displayName?: string;
    phoneNumber?: string;
    role: string;
  }) => Promise<AuthUser>;
  updateAuthUser: (
    uid: string,
    userData: {
      email?: string;
      displayName?: string;
      phoneNumber?: string;
      disabled?: boolean;
      role?: string;
    },
  ) => Promise<AuthUser>;
  deleteAuthUser: (uid: string) => Promise<void>;
  fetchAuthUsers: () => Promise<void>;
  fetchAuthUser: (uid: string) => Promise<AuthUser>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authUsers, setAuthUsers] = useState<AuthUser[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      setUser(user);
      if (user) {
        try {
          // Get the ID token to access custom claims
          const idTokenResult = await user.getIdTokenResult();
          const role = (idTokenResult.claims.role as string) || null;
          setUserRole(role);
        } catch (error) {
          console.error('Error getting user role:', error);
          setUserRole(null);
        }
      } else {
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // User state will be updated by onAuthStateChanged
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (error) {
      throw error;
    }
  };

  const createUser = async (name: string, email: string, password: string): Promise<User> => {
    try {
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Update the user's display name
      await updateProfile(userCredential.user, {
        displayName: name,
      });

      // Add to local authUsers list
      const newAuthUser: AuthUser = {
        uid: userCredential.user.uid,
        email: userCredential.user.email || '',
        displayName: userCredential.user.displayName,
        emailVerified: userCredential.user.emailVerified,
        disabled: false,
        creationTime: userCredential.user.metadata.creationTime || '',
        lastSignInTime: userCredential.user.metadata.lastSignInTime || null,
      };
      setAuthUsers(prev => [newAuthUser, ...prev]);

      // Return the created user
      return userCredential.user;
    } catch (error) {
      throw error;
    }
  };

  const fetchAuthUser = useCallback(async (uid: string): Promise<AuthUser> => {
    try {
      const response = await api.get(`/api/auth/admin/users/${uid}`);
      const user = response.data.data;
      return user;
    } catch (error) {
      console.error('Error fetching auth user:', error);
      throw error;
    }
  }, []);

  const fetchAuthUsers = useCallback(async () => {
    try {
      const response = await api.get('/api/auth/admin/users');
      const users = response.data.data.users.map((user: any) => ({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        phoneNumber: user.phoneNumber,
        emailVerified: user.emailVerified,
        disabled: user.disabled,
        customClaims: user.customClaims,
        metadata: user.metadata,
        // For backward compatibility
        creationTime: user.metadata?.creationTime || '',
        lastSignInTime: user.metadata?.lastSignInTime || null,
      }));
      setAuthUsers(users);
    } catch (error) {
      console.error('Error fetching auth users:', error);
      throw error;
    }
  }, []);

  const createAuthUser = useCallback(
    async (userData: {
      email: string;
      password?: string;
      displayName?: string;
      phoneNumber?: string;
      role: string;
    }): Promise<AuthUser> => {
      try {
        const response = await api.post('/api/auth/admin/users', userData);
        const newUser = response.data.data;

        const authUser: AuthUser = {
          uid: newUser.uid,
          email: newUser.email,
          displayName: newUser.displayName,
          phoneNumber: newUser.phoneNumber,
          emailVerified: newUser.emailVerified,
          disabled: newUser.disabled,
          customClaims: newUser.customClaims,
          metadata: newUser.metadata,
          // For backward compatibility
          creationTime: newUser.metadata?.creationTime || '',
          lastSignInTime: newUser.metadata?.lastSignInTime || null,
        };

        // Add to local state
        setAuthUsers(prev => [authUser, ...prev]);
        return authUser;
      } catch (error) {
        console.error('Error creating auth user:', error);
        throw error;
      }
    },
    [],
  );

  const updateAuthUser = useCallback(
    async (
      uid: string,
      userData: {
        email?: string;
        displayName?: string;
        phoneNumber?: string;
        disabled?: boolean;
        role?: string;
      },
    ): Promise<AuthUser> => {
      try {
        const response = await api.put(`/api/auth/admin/users/${uid}`, userData);
        const updatedUser = response.data.data;

        const authUser: AuthUser = {
          uid: updatedUser.uid,
          email: updatedUser.email,
          displayName: updatedUser.displayName,
          phoneNumber: updatedUser.phoneNumber,
          emailVerified: updatedUser.emailVerified,
          disabled: updatedUser.disabled,
          customClaims: updatedUser.customClaims,
          metadata: updatedUser.metadata,
          // For backward compatibility
          creationTime: updatedUser.metadata?.creationTime || '',
          lastSignInTime: updatedUser.metadata?.lastSignInTime || null,
        };

        // Update local state
        setAuthUsers(prev => prev.map(user => (user.uid === uid ? authUser : user)));
        return authUser;
      } catch (error) {
        console.error('Error updating auth user:', error);
        throw error;
      }
    },
    [],
  );

  const deleteAuthUser = useCallback(async (uid: string): Promise<void> => {
    try {
      await api.delete(`/api/auth/admin/users/${uid}`);

      // Remove from local state
      setAuthUsers(prev => prev.filter(user => user.uid !== uid));
    } catch (error) {
      console.error('Error deleting auth user:', error);
      throw error;
    }
  }, []);

  const value = {
    user,
    loading,
    authUsers,
    userRole,
    login,
    logout,
    createUser,
    createAuthUser,
    updateAuthUser,
    deleteAuthUser,
    fetchAuthUsers,
    fetchAuthUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
